<template>
	<div
		class="min-h-screen flex flex-col transition-colors duration-300 font-['Inter',sans-serif]"
		:class="isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]'">

		<NavBarComponent :isLight="isLight" />

		<!-- Imigongo background patterns -->
		<div class="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
			<svg class="absolute -top-10 -right-10 w-[420px] h-[420px]"
				:style="{ opacity: isLight ? 0.07 : 0.06 }"
				viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
					<rect x="40"  y="0"   width="28" height="28" transform="rotate(45 40  0)"   />
					<rect x="80"  y="0"   width="28" height="28" transform="rotate(45 80  0)"   />
					<rect x="120" y="0"   width="28" height="28" transform="rotate(45 120 0)"   />
					<rect x="20"  y="40"  width="28" height="28" transform="rotate(45 20  40)"  />
					<rect x="60"  y="40"  width="28" height="28" transform="rotate(45 60  40)"  />
					<rect x="100" y="40"  width="28" height="28" transform="rotate(45 100 40)"  />
					<rect x="140" y="40"  width="28" height="28" transform="rotate(45 140 40)"  />
					<rect x="40"  y="80"  width="28" height="28" transform="rotate(45 40  80)"  />
					<rect x="80"  y="80"  width="28" height="28" transform="rotate(45 80  80)"  />
					<rect x="120" y="80"  width="28" height="28" transform="rotate(45 120 80)"  />
					<rect x="20"  y="120" width="20" height="20" transform="rotate(45 20  120)" />
					<rect x="60"  y="120" width="20" height="20" transform="rotate(45 60  120)" />
					<rect x="100" y="120" width="20" height="20" transform="rotate(45 100 120)" />
					<rect x="140" y="120" width="20" height="20" transform="rotate(45 140 120)" />
				</g>
			</svg>
			<svg class="absolute -bottom-10 -left-10 w-[280px] h-[280px]"
				:style="{ opacity: isLight ? 0.06 : 0.05 }"
				viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
				<g :stroke="isLight ? '#0f1f1c' : '#a8d5cc'" stroke-width="3" fill="none">
					<rect x="60" y="10" width="64" height="64" transform="rotate(45 60 10)" />
					<rect x="60" y="22" width="46" height="46" transform="rotate(45 60 22)" />
					<rect x="60" y="34" width="28" height="28" transform="rotate(45 60 34)" />
					<rect x="60" y="46" width="10" height="10" transform="rotate(45 60 46)" />
				</g>
			</svg>
		</div>

		<main class="relative flex-grow pt-24 pb-20">
			<div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

				<!-- Page header -->
				<div class="mb-10">
					<div class="inline-flex items-center gap-2 mb-4 text-[11px] font-semibold tracking-widest uppercase border rounded-full px-4 py-1.5"
						:class="isLight
							? 'text-[#0d6b5e] bg-[#1a9b8a]/10 border-[#1a9b8a]/30'
							: 'text-[#a8d5cc] bg-[#1a9b8a]/10 border-[#1a9b8a]/25'">
						<span class="w-1.5 h-1.5 rounded-full bg-[#1a9b8a]"></span>
						{{ $t('privacy.badge') }}
					</div>
					<h1 class="font-['Montserrat'] font-extrabold text-[clamp(28px,4vw,44px)] leading-tight tracking-tight mb-3"
						:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
						{{ $t('privacy.headline') }}<br />
						<em class="not-italic" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">{{ $t('privacy.highlight') }}</em>
					</h1>
					<p class="text-sm font-medium"
						:class="isLight ? 'text-[#0f1f1c]/45' : 'text-white/35'">
						{{ $t('privacy.lastUpdated') }}
					</p>
				</div>

				<!-- Card wrapper -->
				<div class="rounded-2xl border overflow-hidden"
					:class="isLight ? 'bg-white/60 border-[#1a9b8a]/15' : 'bg-white/[0.03] border-white/[0.07]'">

					<!-- ── PRIVACY POLICY ── -->
					<div class="px-6 sm:px-8 pt-8 pb-6">
						<!-- Section label -->
						<div class="flex items-center gap-3 mb-6">
							<div class="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
								:class="isLight ? 'bg-[#1a9b8a]/15' : 'bg-[#1a9b8a]/10'">
								<svg class="w-4 h-4" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
								</svg>
							</div>
							<h2 class="font-['Montserrat'] font-bold text-lg tracking-tight"
								:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
								Privacy Policy
							</h2>
						</div>

						<div class="space-y-5">

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									1. Information We Collect
								</h4>
								<p class="text-sm leading-relaxed mb-2"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									We collect the following personal information when you use our website or purchase a ticket:
								</p>
								<ul class="space-y-1">
									<li v-for="item in ['Name', 'Email Address', 'Phone Number']" :key="item"
										class="flex items-center gap-2 text-sm"
										:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
										<span class="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-[#1a9b8a]"></span>
										{{ item }}
									</li>
								</ul>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									2. How We Use Your Information
								</h4>
								<p class="text-sm leading-relaxed mb-2"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									We use your personal information to:
								</p>
								<ul class="space-y-1">
									<li v-for="item in [
										'Process ticket purchases and manage event registrations.',
										'Send confirmation emails and event updates.',
										'Provide customer support.',
										'Send newsletters and marketing emails (you may unsubscribe at any time).',
										'Improve our services and user experience.',
									]" :key="item"
										class="flex items-start gap-2 text-sm"
										:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
										<span class="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 bg-[#1a9b8a]"></span>
										{{ item }}
									</li>
								</ul>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									3. Data Sharing &amp; Security
								</h4>
								<p class="text-sm leading-relaxed mb-2"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									We do not sell or rent your personal information to third parties. Your data is stored securely, and we take appropriate measures to protect it from unauthorized access.
								</p>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									We may share your information with event organizers or third-party service providers only for necessary business operations.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									4. Your Rights
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									You can request to access, correct, or delete your personal information. You can unsubscribe from marketing communications at any time.
								</p>
							</div>

							<p class="text-sm"
								:class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/45'">
								For any privacy-related concerns, contact us at
								<a href="mailto:info@trekko.xyz"
									class="font-semibold transition-colors duration-200"
									:class="isLight ? 'text-[#1a9b8a] hover:text-[#0d6b5e]' : 'text-[#a8d5cc] hover:text-white'">
									info@trekko.xyz
								</a>.
							</p>
						</div>
					</div>

					<!-- Divider -->
					<div class="mx-6 sm:mx-8 border-t"
						:class="isLight ? 'border-black/[0.06]' : 'border-white/[0.06]'"></div>

					<!-- ── TERMS & CONDITIONS ── -->
					<div class="px-6 sm:px-8 pt-6 pb-8">
						<!-- Section label -->
						<div class="flex items-center gap-3 mb-6">
							<div class="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
								:class="isLight ? 'bg-[#1a9b8a]/15' : 'bg-[#1a9b8a]/10'">
								<svg class="w-4 h-4" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
								</svg>
							</div>
							<h2 class="font-['Montserrat'] font-bold text-lg tracking-tight"
								:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
								Terms and Conditions
							</h2>
						</div>

						<div class="space-y-5">

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									1. Ticket Purchase &amp; Refund Policy
								</h4>
								<p class="text-sm leading-relaxed mb-2"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									All ticket purchases are <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">final</strong> and <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">non-refundable</strong>, except in cases of event cancellation.
								</p>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									Tickets are <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">non-transferable</strong> unless explicitly permitted by the organizer. We are not responsible for lost or stolen tickets.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									2. Age Requirement
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									By purchasing a ticket, you confirm that you are <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">at least 18 years old</strong>. Minors under 18 must be accompanied by an adult if the event allows underage entry.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									3. Marketing &amp; Communication Consent
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									By purchasing a ticket, you agree that we may save your <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">contact details</strong> (name, email, and phone number) for newsletters and marketing purposes. You can opt out of marketing emails anytime by clicking "Unsubscribe" in the email footer.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									4. Event Cancellation or Changes
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									We reserve the right to <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">cancel, modify, or reschedule</strong> events due to unforeseen circumstances. If an event is canceled, users may be eligible for a refund or ticket exchange.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									5. Liability Disclaimer
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									We are not responsible for any <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">injuries, damages, or lost property</strong> at the event. Attendees participate <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">at their own risk</strong>.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									6. Prohibited Conduct
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									Users must not resell tickets at inflated prices (scalping). Harassment, disruptive behavior, or illegal activities at events are strictly prohibited.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									7. Intellectual Property
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									All event-related content (logos, branding, and materials) are owned by us and may not be used without permission.
								</p>
							</div>

							<div>
								<h4 class="text-[11px] font-semibold tracking-widest uppercase mb-2"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									8. Governing Law &amp; Dispute Resolution
								</h4>
								<p class="text-sm leading-relaxed"
									:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'">
									These terms are governed by the laws of <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">Italy</strong>. Any disputes must be resolved in the courts of <strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">Italy</strong>.
								</p>
							</div>

							<p class="text-sm"
								:class="isLight ? 'text-[#0f1f1c]/60' : 'text-white/45'">
								For any questions about these Terms and Conditions, contact us at
								<a href="mailto:info@trekko.xyz"
									class="font-semibold transition-colors duration-200"
									:class="isLight ? 'text-[#1a9b8a] hover:text-[#0d6b5e]' : 'text-[#a8d5cc] hover:text-white'">
									info@trekko.xyz
								</a>.
							</p>
						</div>
					</div>
				</div>

				<!-- Acknowledgement banner -->
				<div class="mt-6 rounded-2xl border px-6 py-4 flex items-start gap-3"
					:class="isLight ? 'bg-[#1a9b8a]/8 border-[#1a9b8a]/20' : 'bg-[#1a9b8a]/8 border-[#1a9b8a]/15'">
					<svg class="w-4 h-4 flex-shrink-0 mt-0.5" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
					</svg>
					<p class="text-sm leading-relaxed"
						:class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/50'">
						{{ $t('privacy.acknowledgement') }}
						<strong class="font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">{{ $t('privacy.acknowledgementBold') }}</strong>.
					</p>
				</div>
			</div>
		</main>

		<FooterComponent :isLight="isLight" @toggle-mode="isLight = !isLight" />
	</div>
</template>

<script>
import NavBarComponent from "@/components/NavBarComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import { useTheme } from "@/composables/useTheme";

export default {
	components: {
		NavBarComponent,
		FooterComponent,
	},
	setup() {
        const { isLight, toggleMode } = useTheme();
        return { isLight, toggleMode };
    },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");
</style>